/**
 * Body-weight trajectory engine (pure functions, no I/O).
 *
 * 1. OBSERVED EXPENDITURE ("adaptive TDEE")
 *    Energy balance: intake − expenditure = ρ · dW/dt
 *    so  TDEE ≈ mean logged intake − ρ · (weight slope in kg/day).
 *    The weight slope comes from least-squares regression over the window,
 *    which also gives a standard error; that SE becomes the ± band.
 *
 * 2. ENERGY DENSITY OF WEIGHT CHANGE (ρ): Hall 2008, Int J Obes 32:573
 *    ρ = p·ρL + (1 − p)·ρF,  ρF = 39.5 MJ/kg,  ρL = 7.6 MJ/kg
 *    p (lean share of the change) from the Forbes relation, instantaneous
 *    form: p = C / (C + FM), C = 10.4 kg. Fatter people lose a smaller
 *    lean share, so each kg lost costs more energy.
 *    With no body-fat data we fall back to the classic 3500 kcal/lb
 *    (≈7700 kcal/kg), which Hall shows fits people with >30 kg fat but
 *    overstates the deficit needed for leaner people.
 *
 * 3. EXPENDITURE FOLLOWS WEIGHT: Hall et al. 2011, Lancet 378:826
 *    Rule of thumb for an average overweight adult: each 100 kJ/day
 *    (~24 kcal/day) of sustained intake change → ~1 kg eventual weight
 *    change. So expenditure moves ~24 kcal/day per kg of weight change,
 *    which gives the slowing, curved (not straight-line) projection.
 *    This is a simplification of Hall's full model: it does not model
 *    extra adaptive thermogenesis or activity changes.
 */

export const KCAL_PER_MJ = 239.006;
export const RHO_FAT_KCAL_PER_KG = 39.5 * KCAL_PER_MJ;   // ≈ 9441
export const RHO_LEAN_KCAL_PER_KG = 7.6 * KCAL_PER_MJ;   // ≈ 1816
export const FORBES_C_KG = 10.4;
export const RULE_OF_THUMB_KCAL_PER_KG = 3500 / 0.45359237; // 3500 kcal/lb ≈ 7716 kcal/kg
export const DEFAULT_EXPENDITURE_SLOPE = 100 / 1000 * KCAL_PER_MJ; // 100 kJ/day per kg ≈ 23.9 kcal/day/kg

export const CITATIONS = [
  { key: 'hall2008', text: 'Hall KD. What is the required energy deficit per unit weight loss? Int J Obes. 2008;32(3):573-576.', doi: '10.1038/sj.ijo.0803720' },
  { key: 'hall2011', text: 'Hall KD, Sacks G, Chandramohan D, et al. Quantification of the effect of energy imbalance on bodyweight. Lancet. 2011;378(9793):826-837.', doi: '10.1016/S0140-6736(11)60812-X' },
  { key: 'forbes2000', text: 'Forbes GB. Body fat content influences the body composition response to nutrition and exercise. Ann N Y Acad Sci. 2000;904:359-365.', doi: '10.1111/j.1749-6632.2000.tb06482.x' },
] as const;

// ---------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------
export interface DayInput {
  /** YYYY-MM-DD (user's local calendar day). */
  date: string;
  /** Total logged kcal for the day; null/undefined = not logged. */
  calories?: number | null;
  /** Morning (first) weight of the day in kg; null/undefined = no weigh-in. */
  weightKg?: number | null;
  bodyFatPct?: number | null;
  leanMassKg?: number | null;
  /** Meals logged that day. 0 means "not logged" even if calories is 0. */
  mealCount?: number | null;
}

export interface TrajectoryInput {
  days: DayInput[];
  /** Optional override for current body composition. */
  current?: { bodyFatPct?: number | null; leanMassKg?: number | null } | null;
  target?: { weightKg?: number | null; bodyFatPct?: number | null; date?: string | null } | null;
  /** Intake to project with. Defaults to observed average intake. */
  planDailyCalories?: number | null;
  horizonDays?: number;
  options?: {
    /** kcal/day of expenditure change per kg of weight change. */
    expenditureSlopeKcalPerKgPerDay?: number;
    /** Minimum span between first and last weigh-in for an "ok" TDEE. */
    minSpanDays?: number;
    /** Use this TDEE instead of the observed one (e.g. from a BMR formula before enough data exists). */
    tdeeOverrideKcal?: number | null;
  } | null;
}

export type TdeeStatus = 'ok' | 'low_confidence' | 'insufficient_data' | 'override';

export interface WeightRegression {
  n: number;
  slopeKgPerDay: number;
  slopeSeKgPerDay: number | null;
  interceptKg: number;
  residualSdKg: number | null;
  rSquared: number | null;
}

export interface TrajectoryPoint {
  day: number;
  date: string;
  weightKg: number;
  weightLowKg: number | null;
  weightHighKg: number | null;
  fatMassKg: number | null;
  leanMassKg: number | null;
  bodyFatPct: number | null;
}

export interface TrajectoryResult {
  window: {
    firstDate: string | null;
    lastDate: string | null;
    spanDays: number;
    weighIns: number;
    loggedIntakeDays: number;
    intakeCoverage: number;
  };
  trend: {
    currentWeightKg: number | null;
    smoothedWeightKg: number | null;
    weeklyChangeKg: number | null;
    weeklyChangePct: number | null;
    regression: WeightRegression | null;
  };
  energy: {
    meanIntakeKcal: number | null;
    energyDensityKcalPerKg: number;
    energyDensityMethod: 'hall_forbes' | 'rule_of_thumb_3500_per_lb';
    tdeeKcal: number | null;
    tdee95Low: number | null;
    tdee95High: number | null;
    tdeeStatus: TdeeStatus;
    tdeeReasons: string[];
  };
  bodyComposition: {
    fatMassKg: number | null;
    leanMassKg: number | null;
    bodyFatPct: number | null;
    leanShareOfChange: number | null;
  };
  projection: {
    dailyCaloriesUsed: number | null;
    horizonDays: number;
    points: TrajectoryPoint[];
    targetWeightReachedOn: string | null;
    targetBodyFatReachedOn: string | null;
  } | null;
  requiredIntakeForTarget: {
    targetDate: string;
    /** For a body-fat goal: the projected weight on the target date. */
    targetWeightKg: number;
    dailyCalories: number;
    dailyDeficitVsTdee: number;
    impliedWeeklyChangePct: number;
    /** What the intake was solved for: the target weight, or (with no target weight) the target body-fat %. */
    basis: 'weight' | 'body_fat';
    targetBodyFatPct: number | null;
  } | null;
  warnings: string[];
  assumptions: string[];
  citations: typeof CITATIONS;
}

// ---------------------------------------------------------------------
// Date helpers (UTC day arithmetic on YYYY-MM-DD strings)
// ---------------------------------------------------------------------
const DAY_MS = 86_400_000;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function dayNumber(date: string): number {
  if (!DATE_RE.test(date)) throw new RangeError(`Invalid date "${date}" (expected YYYY-MM-DD).`);
  const ms = Date.parse(`${date}T00:00:00Z`);
  if (Number.isNaN(ms) || new Date(ms).toISOString().slice(0, 10) !== date) {
    throw new RangeError(`Invalid date "${date}".`);
  }
  return Math.round(ms / DAY_MS);
}

export function dateFromDayNumber(n: number): string {
  return new Date(n * DAY_MS).toISOString().slice(0, 10);
}

// ---------------------------------------------------------------------
// Building blocks
// ---------------------------------------------------------------------
export function leanShare(fatMassKg: number): number {
  return FORBES_C_KG / (FORBES_C_KG + Math.max(fatMassKg, 0.5));
}

export function energyDensity(fatMassKg: number | null): number {
  if (fatMassKg === null) return RULE_OF_THUMB_KCAL_PER_KG;
  const p = leanShare(fatMassKg);
  return p * RHO_LEAN_KCAL_PER_KG + (1 - p) * RHO_FAT_KCAL_PER_KG;
}

export function linearRegression(points: Array<{ x: number; y: number }>): WeightRegression | null {
  const n = points.length;
  if (n < 2) return null;
  let sx = 0, sy = 0;
  for (const p of points) { sx += p.x; sy += p.y; }
  const mx = sx / n, my = sy / n;
  let sxx = 0, sxy = 0, syy = 0;
  for (const p of points) {
    const dx = p.x - mx, dy = p.y - my;
    sxx += dx * dx; sxy += dx * dy; syy += dy * dy;
  }
  if (sxx === 0) return null;
  const slope = sxy / sxx;
  const intercept = my - slope * mx;
  let ssr = 0;
  for (const p of points) {
    const r = p.y - (intercept + slope * p.x);
    ssr += r * r;
  }
  const residualSd = n > 2 ? Math.sqrt(ssr / (n - 2)) : null;
  const slopeSe = residualSd !== null ? residualSd / Math.sqrt(sxx) : null;
  const rSquared = syy > 0 ? 1 - ssr / syy : null;
  return { n, slopeKgPerDay: slope, slopeSeKgPerDay: slopeSe, interceptKg: intercept, residualSdKg: residualSd, rSquared };
}

/** Exponentially smoothed weight (α per day, carried across missing days). */
export function smoothedWeight(series: Array<{ x: number; y: number }>, alpha = 0.1): number | null {
  if (series.length === 0) return null;
  const first = series[0]!;
  let trend = first.y;
  let lastX = first.x;
  for (let i = 1; i < series.length; i++) {
    const p = series[i]!;
    const gap = Math.max(1, p.x - lastX);
    const a = 1 - Math.pow(1 - alpha, gap); // equivalent weight for a multi-day gap
    trend = trend + a * (p.y - trend);
    lastX = p.x;
  }
  return trend;
}

interface SimState { weightKg: number; fatMassKg: number | null }

/**
 * Simulate day by day. Expenditure = tdee0 + slope·(W − W0).
 * Returns the state at each day 0..days (index = day).
 */
export function simulate(start: SimState, tdee0: number, intake: number, days: number, expenditureSlope: number): SimState[] {
  const out: SimState[] = [{ ...start }];
  let w = start.weightKg;
  let fm = start.fatMassKg;
  for (let d = 1; d <= days; d++) {
    const expenditure = tdee0 + expenditureSlope * (w - start.weightKg);
    const rho = energyDensity(fm);
    const dW = (intake - expenditure) / rho;
    if (fm !== null) {
      const p = leanShare(fm);
      fm = Math.max(0.5, fm + (1 - p) * dW);
    }
    w = Math.max(20, w + dW);
    out.push({ weightKg: w, fatMassKg: fm });
  }
  return out;
}

/** Intake (kcal/day) that lands exactly on targetWeight after `days` days. Bisection; W(days) rises with intake. */
export function solveIntakeForTarget(start: SimState, tdee0: number, targetWeightKg: number, days: number, expenditureSlope: number): number {
  let lo = 0, hi = 10_000;
  for (let i = 0; i < 60; i++) {
    const mid = (lo + hi) / 2;
    const end = simulate(start, tdee0, mid, days, expenditureSlope)[days]!;
    if (end.weightKg < targetWeightKg) lo = mid; else hi = mid;
    if (hi - lo < 0.5) break;
  }
  return (lo + hi) / 2;
}

/**
 * Intake (kcal/day) that lands on a target body-fat % after `days` days. Null without a fat mass.
 * Bisection: with the Forbes split, body-fat % moves the same way as weight (lean mass > C),
 * so it rises with intake.
 */
export function solveIntakeForBodyFat(start: SimState, tdee0: number, targetBodyFatPct: number, days: number, expenditureSlope: number): number | null {
  if (start.fatMassKg === null) return null;
  const pct = (s: SimState): number => (100 * (s.fatMassKg as number)) / s.weightKg;
  let lo = 0, hi = 10_000;
  for (let i = 0; i < 60; i++) {
    const mid = (lo + hi) / 2;
    const end = simulate(start, tdee0, mid, days, expenditureSlope)[days]!;
    if (pct(end) < targetBodyFatPct) lo = mid; else hi = mid;
    if (hi - lo < 0.5) break;
  }
  return (lo + hi) / 2;
}

/**
 * Weight at a target body-fat % if all of the change were fat (lean mass kept). The simulation's
 * Forbes split loses some lean mass on the way, so it reaches the % at a slightly lower weight.
 */
export function weightAtBodyFatKeepingLean(weightKg: number, bodyFatPct: number, targetBodyFatPct: number): number {
  const lean: number = weightKg * (1 - bodyFatPct / 100);
  return lean / (1 - targetBodyFatPct / 100);
}

const r1 = (n: number): number => Math.round(n * 10) / 10;
const r2 = (n: number): number => Math.round(n * 100) / 100;

// ---------------------------------------------------------------------
// Main entry
// ---------------------------------------------------------------------
export function computeTrajectory(input: TrajectoryInput): TrajectoryResult {
  const warnings: string[] = [];
  const assumptions: string[] = [];
  const opts = input.options ?? {};
  const expenditureSlope = opts.expenditureSlopeKcalPerKgPerDay ?? DEFAULT_EXPENDITURE_SLOPE;
  const minSpanDays = opts.minSpanDays ?? 14;
  const horizonDays = Math.min(Math.max(Math.round(input.horizonDays ?? 180), 7), 1095);

  // --- clean + sort days, merging duplicates (last one wins per field) ---
  const byDay = new Map<number, DayInput>();
  for (const d of input.days) {
    const n = dayNumber(d.date);
    const prev = byDay.get(n);
    byDay.set(n, prev ? { ...prev, ...Object.fromEntries(Object.entries(d).filter(([, v]) => v !== undefined && v !== null)) } as DayInput : d);
  }
  const sortedDays = [...byDay.entries()].sort((a, b) => a[0] - b[0]);

  const weights = sortedDays
    .filter(([, d]) => typeof d.weightKg === 'number' && Number.isFinite(d.weightKg) && d.weightKg > 0)
    .map(([x, d]) => ({ x, y: d.weightKg as number }));

  const firstX = weights[0]?.x ?? null;
  const lastX = weights[weights.length - 1]?.x ?? null;
  const spanDays = firstX !== null && lastX !== null ? lastX - firstX : 0;

  // Intake days inside the weigh-in window (intake outside it can't explain the slope).
  const isLogged = (d: DayInput): boolean =>
    typeof d.calories === 'number' && Number.isFinite(d.calories) && d.calories > 0 && (d.mealCount === undefined || d.mealCount === null || d.mealCount > 0);
  const intakeDays = sortedDays.filter(([x, d]) =>
    isLogged(d) && (firstX === null || lastX === null || (x >= firstX && x <= lastX)));
  const meanIntake = intakeDays.length > 0
    ? intakeDays.reduce((acc, [, d]) => acc + (d.calories as number), 0) / intakeDays.length
    : null;
  const intakeCoverage = spanDays > 0 ? Math.min(1, intakeDays.length / (spanDays + 1)) : intakeDays.length > 0 ? 1 : 0;

  // --- trend ---
  const regression = linearRegression(weights);
  const smoothed = smoothedWeight(weights);
  const latestWeight = weights[weights.length - 1]?.y ?? null;
  const currentWeight = regression && lastX !== null && weights.length >= 5
    ? regression.interceptKg + regression.slopeKgPerDay * lastX
    : smoothed ?? latestWeight;

  // --- body composition (explicit override > most recent measured day) ---
  let bodyFatPct: number | null = input.current?.bodyFatPct ?? null;
  let leanMass: number | null = input.current?.leanMassKg ?? null;
  if (bodyFatPct === null && leanMass === null) {
    for (let i = sortedDays.length - 1; i >= 0; i--) {
      const d = sortedDays[i]![1];
      if (typeof d.bodyFatPct === 'number' && d.bodyFatPct > 0) { bodyFatPct = d.bodyFatPct; break; }
      if (typeof d.leanMassKg === 'number' && d.leanMassKg > 0) { leanMass = d.leanMassKg; break; }
    }
  }
  let fatMass: number | null = null;
  if (currentWeight !== null) {
    if (bodyFatPct !== null) fatMass = currentWeight * bodyFatPct / 100;
    else if (leanMass !== null && leanMass < currentWeight) fatMass = currentWeight - leanMass;
  }
  const rho = energyDensity(fatMass);
  const densityMethod = fatMass === null ? 'rule_of_thumb_3500_per_lb' as const : 'hall_forbes' as const;
  if (fatMass === null) {
    assumptions.push('No body-fat data, so 3500 kcal per lb of weight change is assumed. That overstates the deficit needed if you are fairly lean; add a body-fat % for a better estimate.');
  }

  // --- observed TDEE ---
  const tdeeReasons: string[] = [];
  let tdee: number | null = null;
  let tdeeSe: number | null = null;
  let status: TdeeStatus = 'insufficient_data';

  if (typeof opts.tdeeOverrideKcal === 'number' && opts.tdeeOverrideKcal > 0) {
    tdee = opts.tdeeOverrideKcal;
    status = 'override';
    tdeeReasons.push('Using the TDEE you supplied instead of the observed value.');
  } else if (regression && meanIntake !== null && weights.length >= 3) {
    tdee = meanIntake - regression.slopeKgPerDay * rho;
    tdeeSe = regression.slopeSeKgPerDay !== null ? regression.slopeSeKgPerDay * rho : null;
    status = 'ok';
    if (spanDays < minSpanDays) { status = 'low_confidence'; tdeeReasons.push(`Only ${spanDays} days between first and last weigh-in; ${minSpanDays}+ needed. Water and glycogen swings dominate short windows.`); }
    if (weights.length < 8) { status = 'low_confidence'; tdeeReasons.push(`Only ${weights.length} weigh-ins; 8+ recommended.`); }
    if (intakeCoverage < 0.7) { status = 'low_confidence'; tdeeReasons.push(`Food logged on ${Math.round(intakeCoverage * 100)}% of days; 70%+ needed.`); }
  } else {
    if (weights.length < 3) tdeeReasons.push(`Need at least 3 weigh-ins (have ${weights.length}).`);
    if (meanIntake === null) tdeeReasons.push('No logged food intake in the weigh-in window.');
  }
  if (tdee !== null && (tdee < 800 || tdee > 8000)) {
    warnings.push(`Estimated TDEE of ${Math.round(tdee)} kcal is implausible, most likely from incomplete food logs or a scale/water outlier.`);
    if (status === 'ok') status = 'low_confidence';
  }

  // --- projection ---
  const planIntake = input.planDailyCalories ?? meanIntake;
  let projection: TrajectoryResult['projection'] = null;
  let requiredIntake: TrajectoryResult['requiredIntakeForTarget'] = null;
  const lastDayNumber = lastX ?? (sortedDays.length > 0 ? sortedDays[sortedDays.length - 1]![0] : null);

  if (tdee !== null && currentWeight !== null && planIntake !== null && lastDayNumber !== null) {
    const start: SimState = { weightKg: currentWeight, fatMassKg: fatMass };
    const mid = simulate(start, tdee, planIntake, horizonDays, expenditureSlope);
    const band = tdeeSe !== null ? 1.96 * tdeeSe : null;
    // Higher expenditure → lower weight, so the TDEE high bound gives the weight LOW bound.
    const lowW = band !== null ? simulate(start, tdee + band, planIntake, horizonDays, expenditureSlope) : null;
    const highW = band !== null ? simulate(start, tdee - band, planIntake, horizonDays, expenditureSlope) : null;

    const points: TrajectoryPoint[] = [];
    for (let d = 0; d <= horizonDays; d++) {
      if (d % 7 !== 0 && d !== horizonDays) continue;
      const s = mid[d]!;
      points.push({
        day: d,
        date: dateFromDayNumber(lastDayNumber + d),
        weightKg: r2(s.weightKg),
        weightLowKg: lowW ? r2(lowW[d]!.weightKg) : null,
        weightHighKg: highW ? r2(highW[d]!.weightKg) : null,
        fatMassKg: s.fatMassKg !== null ? r2(s.fatMassKg) : null,
        leanMassKg: s.fatMassKg !== null ? r2(s.weightKg - s.fatMassKg) : null,
        bodyFatPct: s.fatMassKg !== null ? r1(100 * s.fatMassKg / s.weightKg) : null,
      });
    }

    let targetWeightReachedOn: string | null = null;
    let targetBodyFatReachedOn: string | null = null;
    const tw = input.target?.weightKg ?? null;
    const tbf = input.target?.bodyFatPct ?? null;
    for (let d = 1; d <= horizonDays; d++) {
      const s = mid[d]!;
      if (tw !== null && targetWeightReachedOn === null &&
          ((tw < currentWeight && s.weightKg <= tw) || (tw > currentWeight && s.weightKg >= tw))) {
        targetWeightReachedOn = dateFromDayNumber(lastDayNumber + d);
      }
      if (tbf !== null && s.fatMassKg !== null && targetBodyFatReachedOn === null && fatMass !== null) {
        const pct = 100 * s.fatMassKg / s.weightKg;
        const startPct = 100 * (fatMass as number) / currentWeight;
        if ((tbf < startPct && pct <= tbf) || (tbf > startPct && pct >= tbf)) {
          targetBodyFatReachedOn = dateFromDayNumber(lastDayNumber + d);
        }
      }
    }
    if (tbf !== null && fatMass === null) {
      warnings.push('A body-fat target was given, but there is no body-fat measurement to project from.');
    }

    projection = {
      dailyCaloriesUsed: Math.round(planIntake),
      horizonDays,
      points,
      targetWeightReachedOn,
      targetBodyFatReachedOn,
    };
    if (input.planDailyCalories == null) {
      assumptions.push('Projection assumes you keep eating your current logged average.');
    }

    // --- intake needed to hit the target (weight, or body-fat % when no weight is set) by the target date ---
    const tDate = input.target?.date ?? null;
    const byBodyFat: boolean = tw === null && tbf !== null && fatMass !== null;
    if ((tw !== null || byBodyFat) && tDate) {
      const daysToTarget = dayNumber(tDate) - lastDayNumber;
      if (daysToTarget < 7) {
        warnings.push('Target date is less than a week after the latest weigh-in; no intake was calculated for it.');
      } else if (daysToTarget > 1095) {
        warnings.push('Target date is more than 3 years out; no intake was calculated for it.');
      } else {
        const needed: number = byBodyFat
          ? solveIntakeForBodyFat(start, tdee, tbf as number, daysToTarget, expenditureSlope) as number
          : solveIntakeForTarget(start, tdee, tw as number, daysToTarget, expenditureSlope);
        const endWeight: number = byBodyFat ? simulate(start, tdee, needed, daysToTarget, expenditureSlope)[daysToTarget]!.weightKg : tw as number;
        const weeklyPct = ((endWeight - currentWeight) / currentWeight) * 100 / (daysToTarget / 7);
        requiredIntake = {
          targetDate: tDate,
          targetWeightKg: r2(endWeight),
          dailyCalories: Math.round(needed),
          dailyDeficitVsTdee: Math.round(tdee - needed),
          impliedWeeklyChangePct: r2(weeklyPct),
          basis: byBodyFat ? 'body_fat' : 'weight',
          targetBodyFatPct: tbf,
        };
        if (weeklyPct < -1) {
          warnings.push(`Hitting that date needs about ${Math.abs(r2(weeklyPct))}% of body weight lost per week. Above ~1%/week, a larger share of the loss tends to come from lean mass.`);
        }
        if (needed < 1200) {
          warnings.push(`That target needs about ${Math.round(needed)} kcal/day, which is very low. Consider a later date, and medical supervision for intakes this low.`);
        }
      }
    }
  } else if (tdee === null) {
    warnings.push('No projection yet: expenditure could not be estimated. Keep logging weight and food, or pass tdeeOverrideKcal.');
  }

  assumptions.push('Short-term water and glycogen swings (sodium, carbs, training) are treated as noise around the trend.');
  assumptions.push('Food logs are assumed complete. Under-logging makes TDEE read low.');

  const weeklyChange = regression ? regression.slopeKgPerDay * 7 : null;

  return {
    window: {
      firstDate: firstX !== null ? dateFromDayNumber(firstX) : null,
      lastDate: lastX !== null ? dateFromDayNumber(lastX) : null,
      spanDays,
      weighIns: weights.length,
      loggedIntakeDays: intakeDays.length,
      intakeCoverage: r2(intakeCoverage),
    },
    trend: {
      currentWeightKg: currentWeight !== null ? r2(currentWeight) : null,
      smoothedWeightKg: smoothed !== null ? r2(smoothed) : null,
      weeklyChangeKg: weeklyChange !== null ? r2(weeklyChange) : null,
      weeklyChangePct: weeklyChange !== null && currentWeight ? r2(100 * weeklyChange / currentWeight) : null,
      regression: regression ? {
        n: regression.n,
        slopeKgPerDay: Math.round(regression.slopeKgPerDay * 10_000) / 10_000,
        slopeSeKgPerDay: regression.slopeSeKgPerDay !== null ? Math.round(regression.slopeSeKgPerDay * 10_000) / 10_000 : null,
        interceptKg: r2(regression.interceptKg),
        residualSdKg: regression.residualSdKg !== null ? r2(regression.residualSdKg) : null,
        rSquared: regression.rSquared !== null ? r2(regression.rSquared) : null,
      } : null,
    },
    energy: {
      meanIntakeKcal: meanIntake !== null ? Math.round(meanIntake) : null,
      energyDensityKcalPerKg: Math.round(rho),
      energyDensityMethod: densityMethod,
      tdeeKcal: tdee !== null ? Math.round(tdee) : null,
      tdee95Low: tdee !== null && tdeeSe !== null ? Math.round(tdee - 1.96 * tdeeSe) : null,
      tdee95High: tdee !== null && tdeeSe !== null ? Math.round(tdee + 1.96 * tdeeSe) : null,
      tdeeStatus: status,
      tdeeReasons,
    },
    bodyComposition: {
      fatMassKg: fatMass !== null ? r2(fatMass) : null,
      leanMassKg: fatMass !== null && currentWeight !== null ? r2(currentWeight - fatMass) : null,
      bodyFatPct: fatMass !== null && currentWeight !== null ? r1(100 * fatMass / currentWeight) : null,
      leanShareOfChange: fatMass !== null ? r2(leanShare(fatMass)) : null,
    },
    projection,
    requiredIntakeForTarget: requiredIntake,
    warnings,
    assumptions,
    citations: CITATIONS,
  };
}
